import type { Flight, Schedule } from './types/flight';

const { DateTimeFormat, NumberFormat } = Intl;

export const date = new DateTimeFormat('nl-NL', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

export const time = new DateTimeFormat('nl-NL', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export const dateTime = new DateTimeFormat('nl-NL', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export const euro = new NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const euroWithZero = new NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
});

export function formatPriceSurcharge(value: number) {
  if (value < 0) {
    return euro.format(value) + ' per persoon';
  } else {
    return euro.format(value) + ' per persoon';
  }
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLocaleLowerCase();
}

export function convertMinutesToDuration(durationInMinutes: number): string {
  const hours = Math.floor(durationInMinutes / 60);
  const minutes = durationInMinutes % 60;
  return `${hours} u ${minutes} min`;
}

function getFullSchedule(durationInMinutes: number, flightTimes: [string, string]): string {
  const [startTime, endTime] = flightTimes.map((time) => new Date(time));

  const timeDiffInMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);

  const totalMinutes = timeDiffInMinutes + durationInMinutes;
  const totalHours = Math.floor(totalMinutes / 60);
  const finalMinutes = totalMinutes % 60;

  return `${totalHours} uur ${finalMinutes} min`;
}

const timeOffsets: Record<string, number> = {
  AMS: 1, // Amsterdam (UTC+1)
  YEG: -7, // Edmonton (UTC-7, MST)
  YYZ: -5, // Toronto (UTC-5, EST)
};

function toUTC(dateTimeString: string, airportCode: string) {
  const [date, time] = dateTimeString.split(' ');
  const [year, month, day] = date.split('-');
  const [hours, minutes, seconds] = time.split(':');

  const localDate = new Date(
    Date.UTC(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours) - timeOffsets[airportCode],
      parseInt(minutes),
      parseInt(seconds)
    )
  );
  return localDate.getTime();
}

function convertScheduleLengthToMessage(scheduleLength: number, destinationName: string): string {
  let scheduleMessage: string;

  if (scheduleLength === 2) {
    scheduleMessage = `1 overstap ${destinationName}`;
  } else if (scheduleLength > 2) {
    scheduleMessage = `${scheduleLength - 1} overstappen`;
  } else {
    scheduleMessage = 'Directe vlucht';
  }

  return scheduleMessage;
}

function calculateFlightDuration(flightSchedule: Schedule[]): string {
  let flightTotalDurationMinutes = 0;

  flightSchedule.forEach((flight) => {
    const departureUTC = toUTC(flight.departure_date_time, flight.airport_origin);
    const arrivalUTC = toUTC(flight.arrival_date_time, flight.airport_destination);

    const durationMinutes = (arrivalUTC - departureUTC) / (1000 * 60);

    flightTotalDurationMinutes += durationMinutes;
  });

  return convertMinutesToDuration(flightTotalDurationMinutes);
}

function calculateFlightSchedule(flightSchedule: Schedule[]): string {
  let schedule: string = '';

  if (flightSchedule.length > 2) {
    const flightDuration = flightSchedule.map(({ duration }) => +duration)?.reduce((acc, cur) => acc + cur, 0);

    const times: [string, string] = [
      flightSchedule[1].arrival_date_time,
      flightSchedule.length > 3 ? flightSchedule[3].departure_date_time : flightSchedule[2].departure_date_time,
    ];

    schedule = getFullSchedule(flightDuration, times);
  }

  return schedule;
}

export function useSchedule(flight: Flight, current?: Flight) {
  const destination = flight.destination;

  const outboundFlight = flight.schedule.filter(({ airport_destination }) => airport_destination === destination);
  const returnFlight = flight.schedule.filter(({ airport_origin }) => airport_origin === destination);

  const outboundFlightScheduleMessage = convertScheduleLengthToMessage(outboundFlight.length, flight.destinationName);
  const returnFlightScheduleMessage = convertScheduleLengthToMessage(returnFlight.length, flight.originName);

  const outboundFlightTotalDurationMinutes: string = calculateFlightDuration(outboundFlight);
  const returnFlightTotalDurationMinutes = calculateFlightDuration(returnFlight);

  const outboundFlightSchedule: string = calculateFlightSchedule(outboundFlight);
  const returnFlightSchedule: string = calculateFlightSchedule(returnFlight);

  let surchargeMessage: string = '';
  if (current) {
    const priceDifference = flight.total_price - current.total_price;
    surchargeMessage = priceDifference !== 0 ? formatPriceSurcharge(priceDifference) : 'Geen toeslag';
  }

  return {
    outboundFlightTotalDurationMinutes,
    returnFlightTotalDurationMinutes,

    outboundFlightScheduleMessage,
    returnFlightScheduleMessage,

    outboundFlight,
    returnFlight,

    outboundFlightSchedule,
    returnFlightSchedule,

    surchargeMessage,
  };
}
